import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { buildPaginationTokens } from '../../../../utils/paginationTokens'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { getCurrentUser } from '../../../../utils/authSession'
import { buildUserSelectOptions, mergeUserOptionsById, fetchOrgFilterOptions } from '../../common/pmOptionUtils'
import { listTaskProjectOptions, listTasks, listTaskUserOptions } from '../api/taskApi'
import { useExportJob } from '../../../../hooks/useExportJob'

const DEFAULT_PAGE_SIZE = 10
const MIN_DATE = '1900-01-01'
const MAX_DATE = '9999-12-31'
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'

function parseAssignedIds(value) {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((part) => Number(String(part || '').trim()))
      .filter((item) => Number.isFinite(item) && item > 0)
  }
  if (Array.isArray(value)) return value.map((item) => Number(item)).filter((item) => Number.isFinite(item) && item > 0)
  return []
}

function toPhotoUrl(path) {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  if (path.startsWith('/')) return `${API_BASE_URL}${path}`
  return `${API_BASE_URL}/${path}`
}

function normalizeStatus(value) {
  const normalized = String(value || 'DRAFT').toUpperCase()
  if (normalized === 'IN_PROGESS') return 'IN_PROGRESS'
  if (['DRAFT', 'IN_PROGRESS', 'COMPLETED', 'CLOSED'].includes(normalized)) return normalized
  return 'DRAFT'
}

function normalizePriority(value) {
  const normalized = String(value || 'MEDIUM').toUpperCase()
  if (['HIGH', 'MEDIUM', 'LOW'].includes(normalized)) return normalized
  return 'MEDIUM'
}


function formatDueDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
}

function deriveSlaState(plannedEnd, status) {
  if (['COMPLETED', 'CLOSED'].includes(String(status || '').toUpperCase())) {
    return { state: 'completed', label: 'COMPLETED' }
  }
  if (!plannedEnd) return { state: 'due_soon', label: 'DUE SOON' }
  const date = new Date(plannedEnd)
  if (Number.isNaN(date.getTime())) return { state: 'due_soon', label: 'DUE SOON' }

  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const end = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return { state: 'overdue', label: 'OVERDUE' }
  if (diffDays === 0) return { state: 'due_today', label: 'DUE TODAY' }
  return { state: 'due_soon', label: 'DUE SOON' }
}

export function useTaskListScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { triggerExport, isExporting } = useExportJob({ module: 'tasks' })

  const currentUser = useMemo(() => getCurrentUser(), [])
  const currentUserId = currentUser?.id || null

  const searchText = searchParams.get('search') || ''
  const projectFilter = searchParams.get('project_id') || 'All'
  const statusFilter = searchParams.get('status') || 'All'
  const priorityFilter = searchParams.get('priority') || 'All'
  const slaFilter = searchParams.get('sla_state') || 'All'
  const companyFilter = searchParams.get('company_id') || 'All'
  const branchFilter = searchParams.get('branch_id') || 'All'
  const departmentFilter = searchParams.get('department_id') || 'All'
  const fromDate = searchParams.get('fromDate') || ''
  const toDate = searchParams.get('toDate') || ''
  const assigneeParam = searchParams.get('assignee_id')
  const assigneeFilter = useMemo(() => {
    if (assigneeParam === null) {
      return currentUserId ? String(currentUserId) : 'All'
    }
    const clean = assigneeParam.trim().toLowerCase()
    if (clean === 'all' || clean === '') {
      return 'All'
    }
    return assigneeParam.trim()
  }, [assigneeParam, currentUserId])
  const page = Number(searchParams.get('page') || 1)

  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [items, setItems] = useState([])
  const [projectOptions, setProjectOptions] = useState([])
  const [userOptions, setUserOptions] = useState(() => {
    if (currentUser?.id) {
      return [
        {
          id: currentUser.id,
          name: currentUser.name || currentUser.email || `User #${currentUser.id}`,
          email: currentUser.email || '',
        },
      ]
    }
    return []
  })
  const [companyOptions, setCompanyOptions] = useState([{ value: 'All', label: 'All Companies' }])
  const [branchOptions, setBranchOptions] = useState([{ value: 'All', label: 'All Branches' }])
  const [departmentOptions, setDepartmentOptions] = useState([{ value: 'All', label: 'All Departments' }])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })

  const queryParams = useMemo(
    () => {
      const params = {
        page,
        page_size: pageSize,
        includeTotal: true,
        archive: false,
        search: searchText.trim(),
        fromDate: fromDate || MIN_DATE,
        toDate: toDate || MAX_DATE,
      }

      if (projectFilter !== 'all' && projectFilter !== 'All') params.project_id = Number(projectFilter)
      if (statusFilter !== 'all' && statusFilter !== 'All') params.status = statusFilter
      if (priorityFilter !== 'all' && priorityFilter !== 'All') params.priority = priorityFilter
      if (slaFilter !== 'all' && slaFilter !== 'All') params.sla_state = slaFilter
      if (assigneeFilter !== 'all' && assigneeFilter !== 'All') params.assignee_id = Number(assigneeFilter)
      if (companyFilter !== 'All') params.company_id = Number(companyFilter)
      if (branchFilter !== 'All') params.branch_id = Number(branchFilter)
      if (departmentFilter !== 'All') params.department_id = Number(departmentFilter)

      return params
    },
    [
      fromDate,
      page,
      pageSize,
      priorityFilter,
      projectFilter,
      searchText,
      slaFilter,
      statusFilter,
      toDate,
      assigneeFilter,
      companyFilter,
      branchFilter,
      departmentFilter,
    ],
  )

  useEffect(() => {
    let active = true

    async function loadRows() {
      setIsLoading(true)
      setError('')
      try {
        const response = await listTasks(queryParams)
        if (!active) return

        setItems(Array.isArray(response?.items) ? response.items : [])
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load tasks.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadRows()
    return () => {
      active = false
    }
  }, [page, pageSize, queryParams])

  useEffect(() => {
    let active = true
    async function loadOptions() {
      try {
        const [projectsRes, usersRes, orgOptions] = await Promise.allSettled([
          listTaskProjectOptions({
            page: 1,
            page_size: 200,
            includeTotal: true,
            archive: false,
            fromDate: MIN_DATE,
            toDate: MAX_DATE,
          }),
          listTaskUserOptions({ page: 1, page_size: 200 }),
          fetchOrgFilterOptions({ pageSize: 200 }),
        ])
        if (!active) return

        if (projectsRes.status === 'fulfilled') {
          setProjectOptions(Array.isArray(projectsRes.value) ? projectsRes.value : [])
        }
        if (usersRes.status === 'fulfilled') {
          const fetchedUsers = Array.isArray(usersRes.value) ? usersRes.value : []
          setUserOptions((prev) => mergeUserOptionsById(prev, fetchedUsers))
        }
        if (orgOptions.status === 'fulfilled') {
          setCompanyOptions(orgOptions.value.companyOptions)
          setBranchOptions(orgOptions.value.branchOptions)
          setDepartmentOptions(orgOptions.value.departmentOptions)
        }
      } catch {
        if (!active) return
      }
    }
    loadOptions()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!Array.isArray(items) || !items.length) return undefined
    let active = true

    async function hydrateAssignedUserLabels() {
      const groupedUserIdsByProject = new Map()
      items.forEach((item) => {
        const projectId = Number(item?.project_id || 0)
        const assignedIds = parseAssignedIds(item?.assigned_to_user_ids || item?.assigned_to_csv)
        if (!assignedIds.length) return
        const key = projectId > 0 ? String(projectId) : '0'
        const bucket = groupedUserIdsByProject.get(key) || new Set()
        assignedIds.forEach((id) => {
          if (Number.isFinite(id) && id > 0) bucket.add(Number(id))
        })
        groupedUserIdsByProject.set(key, bucket)
      })
      if (!groupedUserIdsByProject.size) return

      try {
        const requests = Array.from(groupedUserIdsByProject.entries())
          .map(([projectKey, ids]) => {
            const normalizedIds = Array.from(ids)
            if (!normalizedIds.length) return null
            const params = {
              page: 1,
              page_size: 200,
              user_ids: normalizedIds.join(','),
            }
            if (Number(projectKey) > 0) {
              params.project_id = Number(projectKey)
            }
            return listTaskUserOptions(params)
          })
          .filter(Boolean)

        if (!requests.length) return
        const resolved = await Promise.all(requests)
        if (!active) return
        const hydratedUsers = resolved.flatMap((rows) => (Array.isArray(rows) ? rows : []))
        if (hydratedUsers.length) {
          setUserOptions((prev) => mergeUserOptionsById(prev, hydratedUsers))
        }
      } catch {
        if (!active) return
      }
    }

    hydrateAssignedUserLabels()
    return () => {
      active = false
    }
  }, [items])

  const projectNameById = useMemo(() => {
    const map = new Map()
    projectOptions.forEach((project) => {
      map.set(Number(project.id), project.name || `Project #${project.id}`)
    })
    return map
  }, [projectOptions])

  const userById = useMemo(() => {
    const map = new Map()
    userOptions.forEach((user) => {
      map.set(Number(user.id), user)
    })
    return map
  }, [userOptions])

  const rows = useMemo(() => {
    return items
      .map((item) => {
        const status = normalizeStatus(item.status)
        const priority = normalizePriority(item.priority)
        const projectId = Number(item.project_id)
        const projectName = item.projectName || item.project_name || projectNameById.get(projectId) || `Project #${projectId}`
        const assignedIds = parseAssignedIds(item.assigned_to_user_ids || item.assigned_to_csv)
        const assignedUsers = assignedIds.map((id) => userById.get(id)).filter(Boolean)
        const dueText = formatDueDate(item.planned_end)
        const sla = deriveSlaState(item.planned_end, status)
        const createdAtMs = item.created_at ? new Date(item.created_at).getTime() : 0

        return {
          id: Number(item.id),
          taskCode: item.task_code || '-',
          taskName: item.task_name || '-',
          taskDescription: item.task_description || '',
          projectId,
          projectName,
          status,
          priority,
          dueText,
          sla,
          assignedUsers: assignedUsers.map((user) => ({
            ...user,
            avatarUrl: toPhotoUrl(user.profile_photo_path),
          })),
          assignedCount: assignedIds.length,
          hasDateChanges: Boolean(item.hasDateChanges || (item.date_change_count && Number(item.date_change_count) > 0)),
          createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : 0,
        }
      })
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
  }, [items, projectNameById, userById])

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!rows.length) return 0
    return (page - 1) * pageSize + 1
  }, [page, pageSize, rows.length])

  const rangeEnd = useMemo(() => {
    if (!rows.length) return 0
    return rangeStart + rows.length - 1
  }, [rangeStart, rows.length])

  function updateParam(key, value, resetPage = true) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      const cleanValue = key === 'search' ? String(value || '') : String(value || '').trim()

      if (key === 'assignee_id') {
        if (!cleanValue || cleanValue.toLowerCase() === 'all') {
          next.set(key, 'all')
        } else {
          next.set(key, cleanValue)
        }
      } else if (cleanValue && cleanValue.toLowerCase() !== 'all') {
        next.set(key, cleanValue)
      } else {
        next.delete(key)
      }

      if (resetPage) {
        next.delete('page')
      }
      return next
    }, { replace: true })
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (nextPage === 1) {
        next.delete('page')
      } else {
        next.set('page', String(nextPage))
      }
      return next
    }, { replace: true })
  }

  function goToPreviousPage() {
    goToPage(Math.max(1, page - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) goToPage(page + 1)
  }

  const formattedProjectOptions = useMemo(() => {
    return [
      { value: 'All', label: 'All Projects' },
      ...projectOptions.map((p) => ({ value: String(p.id), label: p.name || p.project_code || `Project ${p.id}` })),
    ]
  }, [projectOptions])

  const formattedUserOptions = useMemo(() => {
    return buildUserSelectOptions(userOptions, 'All Assignees')
  }, [userOptions])

  const searchField = searchParams.get('search_field') || 'Name'

  return {
    state: {
      searchField,
      searchText,
      projectFilter,
      statusFilter,
      priorityFilter,
      slaFilter,
      companyFilter,
      branchFilter,
      departmentFilter,
      fromDate,
      toDate,
      page,
      totalPages,
      totalCount: meta.total,
      hasMore: meta.hasMore,
      isLoading,
      error,
      rows,
      rangeStart,
      rangeEnd,
      searchFieldOptions: [
        { value: 'Name', label: 'Name' },
        { value: 'Code', label: 'Code' },
        { value: 'All', label: 'All' },
      ],
      projectOptions: formattedProjectOptions,
      userOptions: formattedUserOptions,
      companyOptions,
      branchOptions,
      departmentOptions,
      statusOptions: [
        { value: 'All', label: 'All Statuses' },
        { value: 'DRAFT', label: 'Draft' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'CLOSED', label: 'Closed' },
      ],
      priorityOptions: [
        { value: 'All', label: 'All Priorities' },
        { value: 'HIGH', label: 'High' },
        { value: 'MEDIUM', label: 'Medium' },
        { value: 'LOW', label: 'Low' },
      ],
      slaOptions: [
        { value: 'All', label: 'All SLA Statuses' },
        { value: 'due_today', label: 'Due Today' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'due_soon', label: 'Due Soon' },
        { value: 'completed', label: 'Completed' },
      ],
      assigneeFilter,
      pageTokens: buildPaginationTokens(page, totalPages),
    },
    actions: {
      setSearchField: (value) => updateParam('search_field', value),
      setSearchText: (value) => updateParam('search', value),
      setProjectFilter: (value) => updateParam('project_id', value),
      setStatusFilter: (value) => updateParam('status', value),
      setPriorityFilter: (value) => updateParam('priority', value),
      setSlaFilter: (value) => updateParam('sla_state', value),
      setCompanyFilter: (value) => updateParam('company_id', value),
      setBranchFilter: (value) => updateParam('branch_id', value),
      setDepartmentFilter: (value) => updateParam('department_id', value),
      setFromDate: (value) => updateParam('fromDate', value),
      setToDate: (value) => updateParam('toDate', value),
      setAssigneeFilter: (value) => updateParam('assignee_id', value),
      exportTasksToCSV: async () => {
        const exportFilters = { ...queryParams }
        delete exportFilters.page
        delete exportFilters.page_size
        delete exportFilters.includeTotal
        await triggerExport(exportFilters)
      },
      onReset: () => setSearchParams(new URLSearchParams(), { replace: true }),
      goToPreviousPage,
      goToNextPage,
      goToPage,
    },
  }
}



